import { useAdminStore } from "@/stores/admin-store";
import type { ApiResponse } from "@/types/api/common";
import type {
	CreateUserRequest,
	UpdateUserRequest,
	UserListPage,
	UserListQuery,
	UserListResult,
	UserSummary,
} from "@/types/api/user";
import { apiFetch } from "./api-client";

export const userService = {
	/**
	 * `GET /users` (Client `Users/GetAll`) with filters. The API binds query
	 * values by property name (`PageNumber`, `PageSize`, `SearchTerm`, …) and
	 * caps `PageSize` at 50.
	 */
	listUsers: async (query: UserListQuery): Promise<UserListPage> => {
		const params = new URLSearchParams();
		params.set("PageNumber", String(query.pageNumber ?? 1));
		params.set("PageSize", String(query.pageSize ?? 25));
		if (query.searchTerm?.trim())
			params.set("SearchTerm", query.searchTerm.trim());
		if (query.isActive !== undefined)
			params.set("IsActive", String(query.isActive));
		if (query.userGroupId) params.set("UserGroupId", query.userGroupId);
		if (query.invitationStatus)
			params.set("InvitationStatus", query.invitationStatus);
		if (query.userType) params.set("UserType", query.userType);

		const response = await apiFetch<ApiResponse<UserListPage>>(
			`/users?${params}`,
		);
		return response.result;
	},

	getUsers: async (
		page = 1,
		pageSize = 20,
		search = "",
	): Promise<UserListResult> => {
		// Names must match Client `Users/GetAll` properties: the former
		// `page` / `search` were silently ignored (always page 1, no search).
		// PageSize is capped at 50 by the API.
		const params = new URLSearchParams({
			PageNumber: page.toString(),
			PageSize: pageSize.toString(),
		});
		if (search.trim()) params.set("SearchTerm", search.trim());

		// The backend returns a standard PaginatedResult structure with 'items'.
		const response = await apiFetch<ApiResponse<UserListResult>>(
			`/users?${params}`,
		);
		return response.result;
	},

	getUserById: async (userId: string): Promise<UserSummary> => {
		const response = await apiFetch<ApiResponse<UserSummary>>(
			`/users/${userId}`,
		);
		return response.result;
	},

	/**
	 * Create a user in the selected client. `clientId` is injected from the
	 * admin store (same pattern as asset-service); the tenant is also carried
	 * by the `X-Tenant-ID` header that apiFetch adds.
	 */
	createUser: async (
		data: Omit<CreateUserRequest, "clientId">,
	): Promise<UserSummary> => {
		const clientId = useAdminStore.getState().selectedClientId;
		if (!clientId) throw new Error("No client selected");

		const payload: CreateUserRequest = {
			...data,
			clientId,
		};

		const response = await apiFetch<ApiResponse<UserSummary>>("/users", {
			method: "POST",
			body: JSON.stringify(payload),
		});
		return response.result;
	},

	updateUser: async (
		userId: string,
		data: UpdateUserRequest,
	): Promise<UserSummary> => {
		const clientId = useAdminStore.getState().selectedClientId;
		if (!clientId) throw new Error("No client selected");

		const response = await apiFetch<ApiResponse<UserSummary>>(
			`/users/${userId}`,
			{
				method: "PUT",
				body: JSON.stringify(data),
			},
		);
		return response.result;
	},
};
